import Phaser from 'phaser';

export class NutrientSystem {
  private scene: Phaser.Scene;
  private nutrients: Phaser.GameObjects.Sprite[] = [];
  private nextNutrientTime: number = 0;
  private nutrientSpawnRate: number = 2000; // ms
  private worldSize: number = 10000; // Match the increased world size in GameScene
  private maxNutrients: number = 30; // Maximum number of nutrients to have active at once
  
  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }
  
  update(delta: number): void {
    // Check if it's time to spawn a new nutrient
    this.nextNutrientTime -= delta;
    if (this.nextNutrientTime <= 0 && this.nutrients.length < this.maxNutrients) {
      this.spawnNutrient();
      this.nextNutrientTime = this.nutrientSpawnRate;
    }
    
    // Update nutrients
    for (let i = this.nutrients.length - 1; i >= 0; i--) {
      const nutrient = this.nutrients[i];
      
      // Apply some movement to nutrients
      nutrient.x += Math.sin(nutrient.getData('moveTime')) * 0.5;
      nutrient.y += Math.cos(nutrient.getData('moveTime')) * 0.5;
      
      // Update movement time
      nutrient.setData('moveTime', nutrient.getData('moveTime') + 0.05);
      
      // Get distance from camera center
      const camera = this.scene.cameras.main;
      const distanceFromCamera = Phaser.Math.Distance.Between(
        nutrient.x, 
        nutrient.y, 
        camera.scrollX + camera.width / 2 / camera.zoom, 
        camera.scrollY + camera.height / 2 / camera.zoom
      );
      
      // Remove nutrients that are too far from the camera view
      const maxViewDistance = Math.max(camera.width, camera.height) / camera.zoom * 2;
      if (distanceFromCamera > maxViewDistance) {
        nutrient.destroy();
        this.nutrients.splice(i, 1);
      }
    }
    
    // Ensure we always have a minimum number of nutrients
    this.ensureMinimumNutrients();
  }
  
  spawnNutrient(): void {
    // Get camera view bounds
    const camera = this.scene.cameras.main;
    const cameraViewBounds = new Phaser.Geom.Rectangle(
      camera.scrollX - camera.width / (2 * camera.zoom),
      camera.scrollY - camera.height / (2 * camera.zoom),
      camera.width / camera.zoom,
      camera.height / camera.zoom
    );
    
    // Expand the spawn area slightly beyond the camera view
    const spawnBounds = new Phaser.Geom.Rectangle(
      cameraViewBounds.x - 200,
      cameraViewBounds.y - 200,
      cameraViewBounds.width + 400,
      cameraViewBounds.height + 400
    );
    
    // Determine spawn position (random edge of expanded camera view)
    let x, y;
    const side = Math.floor(Math.random() * 4);
    
    switch (side) {
      case 0: // Top
        x = spawnBounds.x + Math.random() * spawnBounds.width;
        y = spawnBounds.y;
        break;
      case 1: // Right
        x = spawnBounds.x + spawnBounds.width;
        y = spawnBounds.y + Math.random() * spawnBounds.height;
        break;
      case 2: // Bottom
        x = spawnBounds.x + Math.random() * spawnBounds.width;
        y = spawnBounds.y + spawnBounds.height;
        break;
      case 3: // Left
        x = spawnBounds.x;
        y = spawnBounds.y + Math.random() * spawnBounds.height;
        break;
    }
    
    // Create nutrient sprite
    const nutrient = this.scene.add.sprite(x ?? 0, y ?? 0, 'nutrient');
    
    // If no texture exists yet, draw a circle
    if (!this.scene.textures.exists('nutrient')) {
      const graphics = this.scene.add.graphics();
      graphics.fillStyle(0x00FF00, 0.8);
      graphics.fillCircle(8, 8, 8);
      graphics.generateTexture('nutrient', 16, 16);
      graphics.destroy();
    }
    
    // Set random scale and alpha
    nutrient.setScale(0.5 + Math.random() * 0.5);
    nutrient.setAlpha(0.7 + Math.random() * 0.3);
    
    // Set data for movement
    nutrient.setData('moveTime', Math.random() * 10);
    nutrient.setData('value', 5 + Math.floor(Math.random() * 10));
    
    // Set depth to ensure nutrients are above background but below cell
    nutrient.setDepth(5);
    
    // Add to array
    this.nutrients.push(nutrient);
  }
  
  private ensureMinimumNutrients(): void {
    // Make sure we always have at least 10 nutrients in the game
    const minNutrients = 10;
    
    if (this.nutrients.length < minNutrients) {
      // Spawn additional nutrients
      const numToSpawn = minNutrients - this.nutrients.length;
      for (let i = 0; i < numToSpawn; i++) {
        this.spawnNutrient();
      }
    }
  }
  
  getNutrients(): Phaser.GameObjects.Sprite[] {
    return this.nutrients;
  }
  
  setNutrientSpawnRate(rate: number): void {
    this.nutrientSpawnRate = rate;
  }
} 